<script setup lang="ts">
/**
 * La radice.
 *
 * Fa una cosa sola: decidere se la schermata corrente va dentro la cornice del
 * pannello o no. L'ingresso e il cambio password obbligato stanno fuori — la
 * prima perché non c'è ancora nessuno da cui prendere il menu, la seconda
 * perché mostrare un menu su cui non si può cliccare nulla è peggio che non
 * mostrarlo.
 *
 * Toast e conferme vivono qui e non dentro la cornice, perché devono
 * sopravvivere anche alle due schermate senza cornice: un errore d'accesso è
 * proprio il caso in cui serve un riscontro.
 */

import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import AppShell from '@/components/shell/AppShell.vue';
import { leggiSessioneSala } from '@/lib/videochiamate';

const route = useRoute();

/**
 * Prima della risoluzione della rotta non si è ancora su nessuna schermata.
 *
 * L'applicazione si monta prima che il router abbia finito: al primo fotogramma
 * `route` è la rotta iniziale vuota, `name` è `undefined` e `meta` è un oggetto
 * senza niente dentro. La condizione qui sotto leggeva quel vuoto come «non è
 * una schermata pubblica» e quindi come «va dentro la cornice», e per chi
 * arrivava **senza sessione** il risultato era un lampo di pannello completo —
 * barra, menu laterale, nome utente vuoto — un istante prima che la guardia lo
 * rimandasse all'ingresso.
 *
 * Non è solo brutto: quel lampo mostra la struttura interna dell'azienda
 * (reparti, sedi, voci di menu) a chi non ha ancora dimostrato di poterla
 * vedere, e monta per un fotogramma componenti che partono a chiedere dati
 * all'API con una sessione che non c'è.
 *
 * `name === undefined` è il modo affidabile di dire «il router non ha ancora
 * deciso»: ogni rotta del pannello ha un nome, quindi non c'è nessun caso vero
 * in cui manchi. La cornice compare quando c'è qualcosa da incorniciare.
 */
const rottaPronta = computed(() => route.name !== undefined);

/**
 * Quando la pagina si disegna **da sola**, senza la cornice del pannello.
 *
 * Tre casi, e il terzo è di natura diversa dai primi due. Le pagine pubbliche e il cambio
 * password non hanno cornice perché **non c'è ancora niente da incorniciare**: non si sa chi
 * sei, o l'unica cosa che puoi fare è quella. Le presenze di linea no: lì si sa benissimo chi
 * sei, e la cornice **non si mette apposta** — è una PWA a sé, installata sul tablet di chi
 * guarda una linea, e una barra laterale con dentro le ferie di tutti sarebbe una porta murata
 * in bella vista su uno schermo che serve a una cosa sola.
 *
 * Si legge da `meta.senzaCornice` e non dal nome della rotta: il nome andava bene per un caso
 * solo, e al secondo sarebbe diventato un elenco di nomi da tenere allineato a mano.
 */
const senzaCornice = computed(
  () =>
    route.meta.pubblica === true ||
    route.meta.senzaCornice === true ||
    route.name === 'cambia-password',
);

/**
 * Il manifesto giusto per la pagina che si sta guardando, e il suo service worker.
 *
 * Il pannello e le presenze di linea sono **due applicazioni installabili diverse**: nomi
 * diversi, ambiti diversi, e sul tablet due icone che aprono due cose. Un manifesto solo non
 * può avere due `start_url`, e una pagina sola — questa, che è un'applicazione a pagina unica —
 * ha un solo `<link rel="manifest">`: perciò si scambia quando la rotta cambia.
 *
 * Il service worker si registra **solo** dentro `/linee`, e solo lì serve: senza un service
 * worker con un gestore di `fetch` il browser non offre «Installa», e senza «Installa» sul
 * tablet non c'è nessuna icona da toccare. Il pannello non ne vuole uno, e dargliene uno
 * vorrebbe dire prendersi una cache da governare per una funzione che nessuno ha chiesto.
 */
const MANIFESTO_LINEE = '/linee.webmanifest';
const MANIFESTO_PANNELLO = '/manifest.webmanifest';
/*
 * «Beste Sale» (2026-10-26): la terza applicazione installabile, per i PC delle sale riunioni.
 * Il suo manifesto vale su `/sala` e — quando questo PC ha una sessione di sala — anche sulla
 * stanza (`/chiamata/...`), perché è lì che la PWA atterra quando intercetta il link di una
 * chiamata, e il browser deve riconoscere la pagina come sua. Scope «/», di proposito: un PC di
 * sala fa solo questo, e i link delle chiamate devono aprirsi dentro l'app (vedi
 * `spec/beste-sale-leggimi.md`).
 */
const MANIFESTO_SALE = '/sale.webmanifest';
watch(
  () => {
    if (route.meta.senzaCornice === true && route.path.startsWith('/linee')) return 'linee';
    if (route.path.startsWith('/sala')) return 'sale';
    if (route.path.startsWith('/chiamata') && leggiSessioneSala() !== null) return 'sale';
    return 'pannello';
  },
  (quale) => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (link !== null) {
      link.href = quale === 'linee' ? MANIFESTO_LINEE : quale === 'sale' ? MANIFESTO_SALE : MANIFESTO_PANNELLO;
    }
    if (quale === 'pannello' || !('serviceWorker' in navigator)) return;
    // Senza `await`: se la registrazione non riesce — niente HTTPS, un browser che non li
    // sostiene — la pagina deve funzionare lo stesso. Si perde l'installabilità, non la
    // schermata, ed è il verso giusto in cui perdere qualcosa.
    if (quale === 'linee') {
      void navigator.serviceWorker.register('/sw-linee.js', { scope: '/linee' }).catch(() => {});
    } else {
      void navigator.serviceWorker.register('/sw-sale.js', { scope: '/sala' }).catch(() => {});
    }
  },
  { immediate: true },
);
</script>

<template>
  <!--
    Finché la rotta non è risolta non si disegna niente: né la cornice né la
    schermata. Non serve uno scheletro — l'attesa è di un fotogramma o due, e
    uno scheletro che compare e sparisce nello stesso battito è più rumoroso
    del vuoto che voleva riempire.
  -->
  <template v-if="rottaPronta">
    <AppShell v-if="!senzaCornice" />
    <RouterView v-else v-slot="{ Component }">
      <Transition name="pagina" mode="out-in">
        <component :is="Component" />
      </Transition>
    </RouterView>
  </template>

  <!-- Fuori dalla condizione: un rifiuto d'accesso arriva mentre la rotta si
       sta ancora risolvendo, e deve poter essere detto lo stesso. -->
  <Toast position="bottom-right" />
  <ConfirmDialog />
</template>

<style>
/*
 * La transizione fra schermate.
 *
 * Otto pixel di salita e una dissolvenza: abbastanza da dare la direzione
 * — la pagina nuova arriva da sotto, come una carta che si posa — e abbastanza
 * poco da non far aspettare. `out-in` e non simultanea: due schermate
 * sovrapposte per duecento millisecondi, in un pannello fatto di tabelle,
 * sono duecento millisecondi di righe che si accavallano.
 *
 * L'uscita dura meno dell'entrata di proposito. È il principio che rende una
 * transizione «rapida» invece che «lenta» a parità di durata totale: ciò che
 * se ne va deve sparire in fretta, ciò che arriva può prendersi il suo tempo.
 *
 * Le durate vengono dai token, quindi `prefers-reduced-motion` le azzera in un
 * punto solo e qui non c'è niente da ricordarsi.
 */
.pagina-enter-active {
  transition:
    opacity var(--beste-duration-normal) var(--beste-ease-enter),
    transform var(--beste-duration-normal) var(--beste-ease-enter);
}

.pagina-leave-active {
  transition:
    opacity var(--beste-duration-fast) var(--beste-ease-exit),
    transform var(--beste-duration-fast) var(--beste-ease-exit);
}

.pagina-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.pagina-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
